import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NavBar from "@/components/NavBar";
import StatusPill from "@/components/StatusPill";
import api, { formatApiError } from "@/lib/api";
import { taskUrgency, sortByUrgency } from "@/lib/taskUrgency";
import {
  ArrowLeft, Mail, Phone, Globe, ShieldCheck, ShieldAlert, ShieldX,
  Briefcase, Star, Banknote, Calendar, MapPin, User as UserIcon, BadgeCheck,
  Key, Lock, Unlock, Copy,
} from "lucide-react";
import { toast } from "sonner";
import AgentAvatar from "@/components/AgentAvatar";

export default function AdminUserDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("active");
  const [resetResult, setResetResult] = useState(null); // {temp_password, emailed}
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data: d } = await api.get(`/admin/users/${userId}`);
      setData(d);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: d } = await api.get(`/admin/users/${userId}`);
        if (active) setData(d);
      } catch (e) {
        if (active) setErr(formatApiError(e));
      }
    })();
    return () => { active = false; };
  }, [userId]);

  const profile = data?.profile;
  const isAgent = profile?.role === "agent";

  const isAdminTarget = profile?.role === "admin";

  const toggleSuspend = async () => {
    if (!profile) return;
    const isSuspending = !profile.suspended;
    let reason = null;
    if (isSuspending) {
      reason = window.prompt("Reason for suspension? (optional, shown in email)") ?? "";
    } else if (!window.confirm(`Reactivate ${profile.name}? They will be able to sign in immediately.`)) {
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/suspend`, {
        suspended: isSuspending,
        reason: reason || null,
      });
      toast.success(isSuspending ? "Account suspended" : "Account reactivated");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (!profile) return;
    if (!window.confirm(
      `Reset password for ${profile.name}?\n\nThis invalidates their existing sessions and forces a new login. ` +
      `A temporary password will be shown to you and emailed to ${profile.email || "the user"}.`,
    )) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/users/${userId}/reset-password`, { notify: true });
      setResetResult(r);
      toast.success("Password reset — temporary password generated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyTemp = async () => {
    if (!resetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temp_password);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  const visibleTasks = useMemo(() => {
    if (!data) return [];
    const bucket = tab === "active" ? data.tasks.active
      : tab === "completed" ? data.tasks.completed
      : data.tasks.cancelled;
    return sortByUrgency(bucket);
  }, [data, tab]);

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/admin?tab=users" className="inline-flex items-center gap-2 text-sm text-[var(--eb-text-dim)] hover:text-[var(--eb-text)]">
          <ArrowLeft className="w-4 h-4" /> Back to users
        </Link>

        {err && <div className="mt-6 eb-card p-6 text-red-700 bg-red-50/40 border-red-200">{err}</div>}

        {!data && !err && (
          <div className="mt-6 eb-card p-10 text-[var(--eb-text-dim)]">Loading…</div>
        )}

        {data && (
          <>
            <section className="mt-6 eb-card p-7" data-testid="admin-user-profile">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4 min-w-0">
                  <AgentAvatar src={profile.photo_url} name={profile.name} size={64} testid="admin-user-avatar" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">
                      {profile.role}{profile.role === "agent" && ` · ${profile.verification_status}`}
                    </div>
                    <h1 className="mt-2 text-3xl font-[800] inline-flex items-center gap-2 flex-wrap">
                      {profile.name}
                      {profile.verification_status === "verified" && (
                        <BadgeCheck className="w-6 h-6 text-[var(--eb-primary)]" />
                      )}
                      {profile.suspended && (
                        <span data-testid="admin-user-suspended-badge" className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-red-100 text-red-800 border border-red-200">
                          Suspended
                        </span>
                      )}
                    </h1>
                    <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[var(--eb-text-dim)]">
                      <Field icon={Mail} label="Email" value={profile.email} testid="admin-user-email" />
                      <Field icon={Phone} label="Phone" value={profile.phone || "—"} testid="admin-user-phone" />
                      <Field icon={Globe} label="Country" value={profile.country || "—"} />
                      <Field icon={MapPin} label="City coverage" value={profile.city || "—"} />
                      <Field icon={UserIcon} label="Auth" value={profile.auth_provider || "email"} />
                      <Field icon={Calendar} label="Joined" value={profile.created_at ? new Date(profile.created_at).toLocaleDateString() : "—"} />
                    </div>
                  </div>
                </div>

                {isAgent && (
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">Agent rating</div>
                    <div className="mt-2 inline-flex items-center gap-1 text-3xl font-[800]">
                      <Star className="w-5 h-5 text-amber-500 fill-current" />
                      {profile.rating?.toFixed(1) || "0.0"}
                    </div>
                    <div className="mt-1 text-xs text-[var(--eb-text-dim)]">{profile.completed_tasks} errand{profile.completed_tasks === 1 ? "" : "s"} completed</div>
                  </div>
                )}
              </div>

              {/* Stats strip */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-5 border-t border-[var(--eb-border)]">
                <Stat label="Total tasks" value={data.stats.total_tasks} />
                <Stat label="Active" value={data.stats.active_count} accent="amber" />
                <Stat label="Completed" value={data.stats.completed_count} accent="emerald" />
                <Stat label="Cancelled" value={data.stats.cancelled_count} accent="stone" />
              </div>
              <div className="mt-3 text-xs text-[var(--eb-text-dim)] inline-flex items-center gap-1">
                <Banknote className="w-4 h-4" /> Lifetime value: <span className="text-[var(--eb-text)] font-[700]">₦{data.stats.total_value_ngn.toLocaleString()}</span>
              </div>
            </section>

            {/* Moderation panel */}
            <section className="mt-6 eb-card p-6" data-testid="admin-moderation-panel">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-[800] inline-flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[var(--eb-primary)]" /> Account moderation
                  </h2>
                  {profile.suspended ? (
                    <p className="mt-1 text-sm text-red-700">
                      Suspended{profile.suspended_at && ` on ${new Date(profile.suspended_at).toLocaleDateString()}`}
                      {profile.suspended_reason && ` · ${profile.suspended_reason}`}
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-[var(--eb-text-dim)]">
                      Account is active. {isAdminTarget && "Admins cannot be suspended."}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="admin-reset-password-btn"
                    onClick={resetPassword}
                    disabled={busy}
                    className="eb-btn eb-btn-ghost"
                    title="Generate a new temp password and email the user"
                  >
                    <Key className="w-4 h-4" /> Reset password
                  </button>
                  {!isAdminTarget && (
                    <button
                      data-testid="admin-suspend-btn"
                      data-state={profile.suspended ? "suspended" : "active"}
                      onClick={toggleSuspend}
                      disabled={busy}
                      className={`eb-btn ${profile.suspended ? "eb-btn-primary" : "eb-btn-ghost"}`}
                    >
                      {profile.suspended ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      {profile.suspended ? "Reactivate account" : "Suspend account"}
                    </button>
                  )}
                </div>
              </div>

              {resetResult && (
                <div className="mt-5 p-4 rounded-xl border border-amber-300 bg-amber-50/60" data-testid="admin-reset-password-result">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700">Temporary password — share with the user, then dismiss</div>
                  <div className="mt-2 flex items-center gap-3">
                    <code className="px-3 py-2 rounded-lg bg-white border border-amber-200 font-[700] text-lg tracking-wider">
                      {resetResult.temp_password}
                    </code>
                    <button
                      data-testid="admin-reset-password-copy"
                      onClick={copyTemp}
                      className="eb-btn eb-btn-ghost !py-2 !px-3 !text-xs"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </button>
                    <button
                      data-testid="admin-reset-password-dismiss"
                      onClick={() => setResetResult(null)}
                      className="eb-btn eb-btn-ghost !py-2 !px-3 !text-xs"
                    >
                      Dismiss
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-amber-800">
                    {resetResult.emailed ? "Sent to the user by email. " : "Email could not be sent (no address on file). "}
                    The user will be forced to sign in again.
                  </p>
                </div>
              )}
            </section>

            {/* Agent profile bio + verification trail */}
            {isAgent && data.agent_profile && (
              <section className="mt-6 eb-card p-6">
                <h2 className="text-lg font-[800]">Agent profile</h2>
                {data.agent_profile.bio && (
                  <p className="mt-3 text-sm text-[var(--eb-text-dim)] italic">&quot;{data.agent_profile.bio}&quot;</p>
                )}
                <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[var(--eb-text-dim)]">
                  {data.agent_profile.id_url && (
                    <a href={data.agent_profile.id_url} target="_blank" rel="noreferrer" className="text-[var(--eb-primary)] underline">View ID document</a>
                  )}
                  {data.agent_profile.selfie_url && (
                    <a href={data.agent_profile.selfie_url} target="_blank" rel="noreferrer" className="text-[var(--eb-primary)] underline">View selfie</a>
                  )}
                </div>
              </section>
            )}

            {/* Ratings + comments for agents */}
            {isAgent && data.ratings.length > 0 && (
              <section className="mt-6 eb-card p-6" data-testid="admin-user-ratings">
                <h2 className="text-lg font-[800] inline-flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" /> Reviews ({data.ratings.length})</h2>
                <ul className="mt-4 space-y-3">
                  {data.ratings.map((r) => (
                    <li key={r.task_id} className="border-l-2 border-amber-300 pl-4">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="inline-flex items-center gap-0.5 text-amber-500">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={`star-${r.task_id}-${i}`} className={`w-3.5 h-3.5 ${i < (r.stars || 0) ? "fill-current" : "opacity-30"}`} />
                          ))}
                        </span>
                        <span className="font-[700]">{r.stars}/5</span>
                        <span className="text-[var(--eb-text-dim)]">· {r.rated_by || "anon"}</span>
                      </div>
                      {r.comment && <p className="mt-1 text-sm text-[var(--eb-text-dim)]">&quot;{r.comment}&quot;</p>}
                      <Link to={`/tasks/${r.task_id}`} className="mt-1 text-[11px] text-[var(--eb-primary)]">
                        {r.task_title} →
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Task history */}
            <section className="mt-6">
              <div className="flex gap-2 mb-4">
                {[
                  { k: "active", label: `Active (${data.stats.active_count})` },
                  { k: "completed", label: `Completed (${data.stats.completed_count})` },
                  { k: "cancelled", label: `Cancelled (${data.stats.cancelled_count})` },
                ].map((t) => (
                  <button
                    key={t.k}
                    data-testid={`admin-user-tab-${t.k}`}
                    onClick={() => setTab(t.k)}
                    className={`eb-btn ${tab === t.k ? "eb-btn-primary" : "eb-btn-ghost"} !py-2 !px-4 !text-xs`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                {visibleTasks.length === 0 ? (
                  <div className="eb-card p-8 text-[var(--eb-text-dim)]">No tasks in this bucket.</div>
                ) : visibleTasks.map((t) => {
                  const u = taskUrgency(t);
                  return (
                    <Link
                      key={t.task_id}
                      to={`/tasks/${t.task_id}`}
                      data-testid="admin-user-task-row"
                      data-urgency={u.bucket}
                      className={`eb-card p-5 flex items-center justify-between gap-3 hover:border-[var(--eb-primary)] transition-colors ${u.ringClass} ${u.bgClass}`}
                    >
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{t.category_label} · {t.city}</div>
                        <div className="font-[700] mt-1 truncate">{t.title}</div>
                        <div className="text-xs text-[var(--eb-text-dim)] mt-1 flex flex-wrap gap-x-3">
                          <span>{isAgent ? `Client: ${t.client_name}` : `Agent: ${t.agent_name || "unassigned"}`}</span>
                          {t.deadline && <span>· Due {new Date(t.deadline).toLocaleDateString()}</span>}
                          <span>· ₦{Number((t.service_fee_ngn || 0) + (t.item_cost_ngn || 0)).toLocaleString()}</span>
                        </div>
                      </div>
                      <StatusPill status={t.status} />
                    </Link>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Field({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[var(--eb-text-faint)] shrink-0" />
      <span className="text-[var(--eb-text-faint)]">{label}:</span>
      <span data-testid={testid} className="text-[var(--eb-text)] truncate">{value}</span>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const colors = {
    amber: "text-amber-700 bg-amber-50/60 border-amber-200",
    emerald: "text-emerald-700 bg-emerald-50/60 border-emerald-200",
    stone: "text-stone-600 bg-stone-50 border-stone-200",
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[accent] || "border-[var(--eb-border)]"}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-[800]">{value}</div>
    </div>
  );
}
